import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useTeamMemberFilter } from '@aibees/shared/views/user-management-view/context/team-member-filter-context';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { createDatePickerOnChange } from './create-date-picker-on-change';

export function useCreditUsageFilters() {
  const { data } = useCurrentSubscriptionApi();
  const { setStartDate, setEndDate, startDate, endDate } =
    useTeamMemberFilter();

  const currentPlan = data?.current;

  const startDateValue = useMemo(
    () =>
      startDate ? dayjs(startDate) : dayjs(currentPlan?.currentPeriodStart),
    [startDate, currentPlan?.currentPeriodStart]
  );

  const endDateValue = useMemo(
    () => (endDate ? dayjs(endDate) : dayjs(currentPlan?.currentPeriodEnd)),
    [endDate, currentPlan?.currentPeriodEnd]
  );

  const handleStartDateChange = useCallback(
    (value: Dayjs | null) => {
      setStartDate(value ? value.toISOString() : undefined);
    },
    [setStartDate]
  );

  const handleEndDateChange = useCallback(
    (value: Dayjs | null) => {
      setEndDate(value ? value.toISOString() : undefined);
    },
    [setEndDate]
  );

  const onStartDateChange = useMemo(
    () => createDatePickerOnChange(handleStartDateChange),
    [handleStartDateChange]
  );
  const onEndDateChange = useMemo(
    () => createDatePickerOnChange(handleEndDateChange),
    [handleEndDateChange]
  );

  const shouldDisableDate = useCallback(
    (date: Dayjs): boolean => {
      return (
        date.isBefore(dayjs(currentPlan?.currentPeriodStart), 'day') ||
        date.isAfter(dayjs(currentPlan?.currentPeriodEnd), 'day')
      );
    },
    [currentPlan?.currentPeriodStart, currentPlan?.currentPeriodEnd]
  );

  return {
    data,
    startDateValue,
    endDateValue,
    onStartDateChange,
    onEndDateChange,
    shouldDisableDate,
  };
}
